import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Divider,
  Link
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  ContactSupport as ContactSupportIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { LEARNING_GROUPS, getGroupForMilieu, BASISSICHERHEIT_MODULES } from '../data/quizResultData';
import { useChecklist } from '../contexts/ChecklistContext';
import { getAvatarForCluster, hasAvatarForCluster } from '../data/avatarData';
import { useAuth } from '../contexts/AuthContext';
import Sidebar, { drawerWidth, collapsedDrawerWidth } from '../components/Sidebar';
import { CHECKLIST_DATA } from '../data/checklistData';

const QuizResults = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { quizResult, resetProgress, checklistItems } = useChecklist();
  const [markedAsDone, setMarkedAsDone] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Load markedAsDone from localStorage
  useEffect(() => {
    const loadMarkedAsDone = () => {
      if (user) {
        const markedKey = `marked_as_done_${user.id}`;
        const saved = localStorage.getItem(markedKey);
        if (saved) {
          try {
            setMarkedAsDone(JSON.parse(saved));
          } catch (e) {
            console.error('Error parsing marked as done:', e);
          }
        }
      }
    };

    loadMarkedAsDone();

    // Listen for storage changes (e.g., when user marks module as done in Checklist page)
    const handleStorageChange = (e) => {
      if (user && e.key === `marked_as_done_${user.id}`) {
        loadMarkedAsDone();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom storage events (for same-tab updates)
    const handleCustomStorageChange = () => {
      if (user) {
        loadMarkedAsDone();
      }
    };
    
    window.addEventListener('localStorageUpdated', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdated', handleCustomStorageChange);
    };
  }, [user]);

  // Get profile data (needed for useMemo hook)
  const milieuName = quizResult?.primary?.name;
  const groupName = milieuName ? getGroupForMilieu(milieuName) : null;
  const profile = groupName ? (LEARNING_GROUPS[groupName] || LEARNING_GROUPS["Der vertrauensvolle Navigator"]) : null;
  const cluster = quizResult?.cluster;

  // Filter out completed modules and add new ones to maintain the list
  // This hook must be called before any early returns
  const filteredRecommendedModules = useMemo(() => {
    if (!profile || !profile.recommendedModules) {
      return [];
    }

    if (!checklistItems || checklistItems.length === 0) {
      return profile.recommendedModules;
    }

    // Get all checklist items from the Basissicherheit category
    const basissicherheitCategory = checklistItems.find(cat => cat.category === 'Basissicherheit');
    if (!basissicherheitCategory) {
      return profile.recommendedModules;
    }

    // Helper function to check if a module is completed
    const isModuleCompleted = (moduleName) => {
      const checklistItem = basissicherheitCategory.items.find(
        item => item.moduleName === moduleName
      );
      if (!checklistItem) return false;
      const isMarkedAsDone = markedAsDone[checklistItem.id] === true;
      const isCompleted = checklistItem.completed === true;
      return isMarkedAsDone && isCompleted;
    };

    // Filter out completed modules from the recommended list
    const activeRecommendedModules = profile.recommendedModules.filter(module => {
      return !isModuleCompleted(module.moduleName);
    });

    // Get all available modules from checklistData (not just the ones in checklistItems)
    const allAvailableModules = CHECKLIST_DATA.find(cat => cat.category === 'Basissicherheit');
    if (!allAvailableModules) {
      return activeRecommendedModules;
    }

    // Get list of already recommended module names
    const recommendedModuleNames = new Set(
      profile.recommendedModules.map(m => m.moduleName)
    );

    // Find modules that are:
    // 1. Not already in the recommended list
    // 2. Not completed
    // 3. Have a moduleName defined
    const availableModulesToAdd = allAvailableModules.items
      .filter(item => 
        item.moduleName && 
        !recommendedModuleNames.has(item.moduleName) &&
        !isModuleCompleted(item.moduleName)
      )
      .map(item => {
        // Create a module object similar to the recommendedModules structure
        // Use a default duration and why text
        return {
          title: item.title,
          duration: "4 Min",
          why: "Empfohlen für deinen Lernpfad",
          moduleName: item.moduleName
        };
      });

    // Add new modules to maintain the original count
    const originalCount = profile.recommendedModules.length;
    const neededCount = originalCount - activeRecommendedModules.length;
    
    if (neededCount > 0 && availableModulesToAdd.length > 0) {
      // Add the needed number of modules
      const modulesToAdd = availableModulesToAdd.slice(0, neededCount);
      return [...activeRecommendedModules, ...modulesToAdd];
    }

    return activeRecommendedModules;
  }, [profile, checklistItems, markedAsDone]);

  if (!user) {
    return null;
  }

  if (!quizResult || !quizResult.primary || !profile) {
    // Redirect to digitale-selbstverteidigung if no result
    navigate('/digitale-selbstverteidigung');
    return null;
  }

  const handleStartLearning = () => {
    navigate('/checklist');
  };

  const handleRetakeQuiz = async () => {
    await resetProgress();
    navigate('/quiz');
  };

  const handleStartModule = (moduleName) => {
    // Navigate to specific module/category
    const module = BASISSICHERHEIT_MODULES[moduleName];
    if (module) {
      navigate(`/checklist/${module.category}`);
    } else {
      navigate('/checklist');
    }
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)',
      minHeight: '100vh', 
      color: '#ffffff',
      position: 'relative',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(33, 150, 243, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(66, 165, 245, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0,
      }
    }}>
      {/* Sidebar */}
      <Sidebar 
        open={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        mobileOpen={mobileSidebarOpen}
        onMobileToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
      />
      
      <Box sx={{ 
        display: 'flex',
        ml: { sm: sidebarOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px` },
        transition: (theme) => theme.transitions.create('margin-left', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        pt: { xs: 2, sm: 3, md: 4 },
        pb: 4,
      }}>
        <Container maxWidth="lg" sx={{ flexGrow: 1, width: '100%' }}>
      {/* Zurück zum Lagebild Link */}
      <Box sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{
            color: '#42a5f5',
            '&:hover': {
              backgroundColor: 'rgba(66, 165, 245, 0.1)',
            },
          }}
        >
          Zurück zum Lagebild
        </Button>
      </Box>
      
      {/* 1. Hero-Bereich */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="overline" sx={{ color: '#b0b0b0', mb: 2, display: 'block' }}>
          Dein Ergebnis, Du bist:
        </Typography>
        <Typography 
          variant="h2" 
          component="h1" 
          sx={{ 
            mb: 3, 
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            lineHeight: 1.2,
            color: '#ffffff'
          }}
        >
          {profile.heroTitle}
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 4, 
            color: '#b0b0b0',
            maxWidth: '700px',
            mx: 'auto',
            fontWeight: 400
          }}
        >
          {profile.heroSubline}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={handleStartLearning}
            sx={{ 
              px: 4, 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              backgroundColor: '#2196f3',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#1976d2',
              }
            }}
          >
            Starte dein Microlearning
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={handleRetakeQuiz}
            sx={{ 
              px: 4, 
              py: 1.5,
              fontSize: '1.1rem',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            Quiz erneut machen
          </Button>
        </Box>
      </Box>

      {/* 3. Kurzprofil */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#ffffff' }}>
            Dein Kurzprofil
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#b0b0b0' }}>
                  So lernst du am liebsten:
                </Typography>
                <Chip 
                  label={profile.learningStyle} 
                  variant="outlined"
                  sx={{ 
                    fontWeight: 500,
                    borderColor: 'rgba(33, 150, 243, 0.5)',
                    color: '#42a5f5'
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#b0b0b0' }}>
                  Was dich motiviert:
                </Typography>
                <Chip 
                  label={profile.motivation} 
                  variant="outlined"
                  sx={{ 
                    fontWeight: 500,
                    borderColor: 'rgba(33, 150, 243, 0.5)',
                    color: '#42a5f5'
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#b0b0b0' }}>
                  Deine Stärke:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(Array.isArray(profile.strength) ? profile.strength : [profile.strength]).map((strength, index) => (
                    <Chip 
                      key={index}
                      label={strength} 
                      variant="outlined"
                      sx={{ 
                        fontWeight: 500,
                        borderColor: 'rgba(76, 175, 80, 0.5)',
                        color: '#4caf50'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#b0b0b0' }}>
                  Achte besonders auf:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(Array.isArray(profile.attention) ? profile.attention : [profile.attention]).map((attention, index) => (
                    <Chip 
                      key={index}
                      label={attention} 
                      variant="outlined"
                      sx={{ 
                        fontWeight: 500,
                        borderColor: 'rgba(255, 152, 0, 0.5)',
                        color: '#ff9800'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 4. Dein persönlicher Lernpfad */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#ffffff' }}>
          Dein persönlicher Lernpfad
        </Typography>
        
        {filteredRecommendedModules.length === 0 ? (
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4caf50' }}>
                🎉 Glückwunsch!
              </Typography>
              <Typography variant="body1" sx={{ color: '#b0b0b0' }}>
                Du hast alle empfohlenen Module abgeschlossen. Du kannst weitere Module in der Checkliste erkunden.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {filteredRecommendedModules.map((module, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(33, 150, 243, 0.3)',
                    border: '1px solid rgba(66, 165, 245, 0.3)',
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#ffffff' }}>
                    {module.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ mb: 2, flexGrow: 1, fontStyle: 'italic', color: '#b0b0b0' }}
                  >
                    Warum für dich: {module.why}
                  </Typography>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleStartModule(module.moduleName)}
                    sx={{ 
                      mt: 'auto',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: '#ffffff',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    Starten
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Avatar Section (wenn verfügbar) */}
      {cluster && hasAvatarForCluster(cluster.name) && (
        <Card sx={{ 
          mb: 4, 
          background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}>
          <CardContent sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#ffffff' }}>
              Deinen Antworten zufolge sollten dich {getAvatarForCluster(cluster.name).maleName} und {getAvatarForCluster(cluster.name).femaleName} auf deinem Weg begleiten:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={getAvatarForCluster(cluster.name).male} 
                  alt={`${cluster.name} - ${getAvatarForCluster(cluster.name).maleName}`}
                  style={{ 
                    width: '120px', 
                    height: '120px', 
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #1976d2',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                />
                <Typography variant="body2" sx={{ mt: 1, color: '#b0b0b0' }}>
                  {getAvatarForCluster(cluster.name).maleName}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={getAvatarForCluster(cluster.name).female} 
                  alt={`${cluster.name} - ${getAvatarForCluster(cluster.name).femaleName}`}
                  style={{ 
                    width: '120px', 
                    height: '120px', 
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #2196f3',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                  }}
                />
                <Typography variant="body2" sx={{ mt: 1, color: '#b0b0b0' }}>
                  {getAvatarForCluster(cluster.name).femaleName}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 5. "Und jetzt?" Abschluss */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(15, 15, 15, 0.9) 100%)',
          border: '1px solid rgba(33, 150, 243, 0.3)',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(33, 150, 243, 0.2)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#ffffff' }}>
          Und jetzt?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: '#b0b0b0' }}>
          Starte am besten mit Modul 1 und beginne deine Reise, um deinen digitalen Alltag sicherer zu machen.
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={<PlayArrowIcon />}
          onClick={handleStartLearning}
          sx={{ 
            px: 4, 
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            backgroundColor: '#2196f3',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#1976d2',
            }
          }}
        >
          Jetzt starten
        </Button>
      </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default QuizResults;

